export const THEME_PREVIEW_CONTEXT_SOURCE = 'automatic-v1';
/** Sample Modules can hold 1–100 fictional questions; the Studio detects these fields to offer the controls. */
export const MAX_PREVIEW_QUESTIONS = 100;
/** Question keys are `content.activity.key` for each card in order; blank entries keep the sample key. */
export const MAX_PREVIEW_QUESTION_KEY_LENGTH = 255;

/** Fictional presentation data for the explicit staging demo build only. */
export interface ThemePreviewSettings {
  version: 1;
  firstName: string;
  courseTitle: string;
  chapterTitle: string;
  moduleTitle: string;
  moduleNumber: number;
  moduleTotal: number;
  questionCount: number;
  questionKeys: string[];
  step: number;
  totalKnown: boolean;
  visit: 'starting' | 'resuming' | 'revisiting';
  locale: string;
  direction: 'ltr' | 'rtl';
  colorScheme: 'light' | 'dark' | 'system';
  reducedMotion: boolean;
  highContrast: boolean;
  simplifiedBackground: boolean;
  textScale: 1 | 1.25 | 1.5 | 2;
  quality: 'low' | 'standard';
  animationIntensity: 'none' | 'low' | 'standard';
  decorationDensity: 'none' | 'low' | 'standard';
  seed: string;
  recording: boolean;
  fixedTime: number | null;
}
export const DEFAULT_PREVIEW_SETTINGS: Readonly<ThemePreviewSettings> = Object.freeze({
  version: 1, firstName: 'Nicole',
  courseTitle: 'Life Plan', chapterTitle: 'Getting started', moduleTitle: 'Orientation',
  moduleNumber: 6, moduleTotal: 20, questionCount: 6, questionKeys: [], step: 0, totalKnown: true, visit: 'starting',
  locale: 'en', direction: 'ltr', colorScheme: 'system', reducedMotion: false, highContrast: false,
  simplifiedBackground: false, textScale: 1, quality: 'standard', animationIntensity: 'standard',
  decorationDensity: 'standard', seed: 'theme-studio-sample', recording: false, fixedTime: null,
});
const object = (input: unknown): Record<string, unknown> => input !== null && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {};
export function normalizePreviewSettings(input: unknown): ThemePreviewSettings {
  const value = object(input), defaults = DEFAULT_PREVIEW_SETTINGS;
  const text = (key: keyof ThemePreviewSettings, max = 160): string => typeof value[key] === 'string' ? Array.from(value[key] as string).filter(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127).join('').slice(0, max) : String(defaults[key]);
  const bool = (key: keyof ThemePreviewSettings): boolean => typeof value[key] === 'boolean' ? value[key] as boolean : Boolean(defaults[key]);
  const number = (key: keyof ThemePreviewSettings, min: number, max: number): number => typeof value[key] === 'number' && Number.isFinite(value[key]) ? Math.min(max, Math.max(min, value[key] as number)) : Number(defaults[key]);
  const moduleTotal = Math.floor(number('moduleTotal', 1, 1000));
  const questionCount = Math.floor(number('questionCount', 1, MAX_PREVIEW_QUESTIONS));
  const keys = Array.isArray(value.questionKeys) ? value.questionKeys.slice(0, MAX_PREVIEW_QUESTIONS) : [];
  const questionKeys = keys.map(key => typeof key === 'string' ? Array.from(key).filter(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127).join('').trim().slice(0, MAX_PREVIEW_QUESTION_KEY_LENGTH) : '');
  return {
    version: 1, firstName: text('firstName', 80),
    courseTitle: text('courseTitle'), chapterTitle: text('chapterTitle'), moduleTitle: text('moduleTitle'),
    moduleNumber: Math.floor(number('moduleNumber', 1, moduleTotal)), moduleTotal, questionCount, questionKeys,
    step: Math.floor(number('step', 0, questionCount)), totalKnown: bool('totalKnown'),
    visit: value.visit === 'resuming' || value.visit === 'revisiting' ? value.visit : 'starting',
    locale: /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(text('locale', 35)) ? text('locale', 35) : 'en',
    direction: value.direction === 'rtl' ? 'rtl' : 'ltr', colorScheme: value.colorScheme === 'dark' || value.colorScheme === 'light' ? value.colorScheme : 'system', reducedMotion: bool('reducedMotion'), highContrast: bool('highContrast'),
    simplifiedBackground: bool('simplifiedBackground'), textScale: value.textScale === 1.25 || value.textScale === 1.5 || value.textScale === 2 ? value.textScale : 1, quality: value.quality === 'low' ? 'low' : 'standard',
    animationIntensity: value.animationIntensity === 'none' || value.animationIntensity === 'low' ? value.animationIntensity : 'standard', decorationDensity: value.decorationDensity === 'none' || value.decorationDensity === 'low' ? value.decorationDensity : 'standard',
    seed: text('seed', 80), recording: bool('recording'), fixedTime: typeof value.fixedTime === 'number' && Number.isFinite(value.fixedTime) ? Math.min(3600, Math.max(0, value.fixedTime)) : null,
  };
}
export const THEME_PREVIEW_PRESETS: Readonly<Record<string, Partial<ThemePreviewSettings>>> = Object.freeze({
  default: DEFAULT_PREVIEW_SETTINGS,
  'first-name': { firstName: 'Alex' },
  anonymous: { firstName: '' },
  'long-text': { firstName: 'Alexandra Charlotte', moduleTitle: 'Making room for the small changes that matter most to you', courseTitle: 'A thoughtful journey towards a more balanced everyday life' },
  rtl: { firstName: 'نور', moduleTitle: 'خطوات صغيرة نحو التوازن', courseTitle: 'رحلتي', locale: 'ar', direction: 'rtl' },
  'reduced-motion': { reducedMotion: true, simplifiedBackground: true, animationIntensity: 'none' },
  'unknown-progress': { totalKnown: false, visit: 'resuming', step: 3 },
  completion: { step: 6 },
  'single-question': { questionCount: 1 },
  'many-questions': { questionCount: 40 },
});

declare global { interface Window { __WF_THEME_PREVIEW__?: unknown; } }
